import React, { useEffect, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { setDictionaryListAction } from '../../../redux/slices/dictionary';
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import {
  BucketKind,
  INamedBucketDefEditMutation,
  INamedBucketDefEditMutationVariables,
  INamedBucketDefFieldsFragment,
  INamedBucketDefsQuery,
  INamedBucketDefsQueryVariables,
  ManagedEntityType,
  PermissionKey,
} from '../../../utils/apollo/graphqlSchema';
import GET_NAMED_BUCKET_DEFS from '../../Formulas/apollo/GET_NAMED_BUCKET_DEFS';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import DictionaryHeader from '../utils/DictionaryHeader';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { NAMED_BUCKET_DEF_ADD_CATEGORY } from '../../../utils/apollo/bucketDefs';
import { EDIT_NAMED_BUCKET_DEF } from '../../Formulas/apollo';
import { useDebounce } from '../../../utils/hooks';
import usePermissionSchemas from '../../PermissionControls/hooks/usePermissionSchemas';
import toastMessage from '../../../utils/toastMessage';
import { ContentLoader } from '../../../components/Loaders';
import DictionaryNoResults from '../DictionaryNoResults';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import { ModalSliceType, openModal } from '../../../redux/slices/modal';
import BinningsTable from './BinningsTable';
import useApplyTags from '../useApplyTags';
import { CategoryEntityType } from '../../../components/Categories/utils';
import { NAMED_BUCKET_DEF_REMOVE_CATEGORY } from '../../../utils/apollo/bucketDefs';
import { RowSelectionState } from '@tanstack/react-table';
import { selectOpenedModalType } from '../../../redux/selectors/modal';
import DeleteBinningModal from './DeleteBinningModal';
import { useGlobalApplyTags } from '../utils/useGlobalApplyTags';

const BinningDictionary = () => {
  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'dictionary',
        name: 'binning',
        id: null,
      }),
    );
  }, [track]);

  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const { selectedSchemaId, permittedSchemaIds } = usePermissionSchemas({
    requestedPermissionKey: PermissionKey.BucketDefRead,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const namedBucketDefs = useAppSelector(
    (state) => state.dictionary.buckets,
  ) as INamedBucketDefFieldsFragment[];

  const isDeleteModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.DeleteBucket, ''),
  );

  const onFetchBuckets = ({ namedBucketDefs }: INamedBucketDefsQuery) => {
    const entities = namedBucketDefs?.entities ?? [];
    dispatch(
      setDictionaryListAction({
        newValues: entities,
        dictionaryType: DictionaryTypeEnum.DictionaryBuckets,
      }),
    );
  };

  const { checkIsPermitted } = usePermission();
  const isPermittedToAssignCategory = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.CategoryCreate,
      PermissionKey.BucketDefEdit,
    ],
    schemaId: selectedSchemaId,
  });

  const isPermittedToEditBinning = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.BucketDefEdit],
    schemaId: selectedSchemaId,
  });

  const isPermittedToDeleteBinning = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.BucketDefDelete],
    schemaId: selectedSchemaId,
  });

  const { loading } = useQuery<
    INamedBucketDefsQuery,
    INamedBucketDefsQueryVariables
  >(GET_NAMED_BUCKET_DEFS, {
    variables: {
      term: debouncedSearchQuery,
      includes: [],
      kinds: [BucketKind.Enum, BucketKind.Range],
      schemaId: selectedSchemaId,
      field: null,
    },
    onCompleted: onFetchBuckets,
  });

  const handleEditMutationComplete = ({
    namedBucketDefEdit,
  }: INamedBucketDefEditMutation) => {
    const { entity, errors } = namedBucketDefEdit;

    if (printGraphqlErrors(errors)) {
      return;
    }

    if (entity) {
      toastMessage({
        message: `${entity.name} has been updated.`,
        type: 'info',
      });
    }
  };

  const [editNamedBucketDef] = useMutation<
    INamedBucketDefEditMutation,
    INamedBucketDefEditMutationVariables
  >(EDIT_NAMED_BUCKET_DEF, {
    onCompleted: handleEditMutationComplete,
  });

  const refreshTable = async () => {
    await client.refetchQueries({
      include: [GET_NAMED_BUCKET_DEFS],
    });
  };

  const onNameChange = async (bucketDefId: string, newValue: string) => {
    const { data } = await editNamedBucketDef({
      variables: {
        input: {
          name: newValue.trim(),
          basicInput: null,
          comment: null,
          description: null,
          bucketDefId,
          categories: null,
        },
      },
    });

    if (data) {
      await refreshTable();
    }
  };

  const onDescriptionChange = async (bucketDefId: string, newValue: string) => {
    const { data } = await editNamedBucketDef({
      variables: {
        input: {
          name: null,
          basicInput: null,
          comment: null,
          description: newValue.trim(),
          bucketDefId,
          categories: null,
        },
      },
    });

    if (data) {
      await refreshTable();
    }
  };

  const handleSubmitNewTags = () => {
    refreshTable();
    setRowSelection({});
  };

  const handleApplyTags = useApplyTags({
    entityType: CategoryEntityType.NamedBucketDef,
    addCategoryMutation: NAMED_BUCKET_DEF_ADD_CATEGORY,
    removeCategoryMutation: NAMED_BUCKET_DEF_REMOVE_CATEGORY,
    managedEntityType: ManagedEntityType.BucketDef,
    getEntityId: (entity: INamedBucketDefFieldsFragment) => entity.id,
    getEntityCategories: (entity: INamedBucketDefFieldsFragment) =>
      entity.categories,
    onSuccess: handleSubmitNewTags,
  });

  const handleGlobalApplyTags = useGlobalApplyTags({
    rowSelection,
    entities: namedBucketDefs,
    selectedSchemaId,
    handleApplyTags,
    getEntityId: (entity) => entity.id,
  });

  const onSetFavorite = async () => {
    await refreshTable();
  };

  const handleDeleteNamedBucket = (
    namedBucketDef: INamedBucketDefFieldsFragment,
  ) => {
    dispatch(
      openModal({ type: ModalSliceType.DeleteBucket, data: namedBucketDef }),
    );
  };

  const selectedEntityCategories = namedBucketDefs
    .filter((bucketDef) => rowSelection[bucketDef.id])
    .map((bucketDef) => ({ categories: bucketDef.categories }));

  return (
    <>
      <DictionaryHeader
        selectedSchemaId={selectedSchemaId}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        title="Binning"
        dictionaryItemCount={namedBucketDefs.length}
        permittedSchemaIds={permittedSchemaIds}
        isPermittedToAssignCategory={isPermittedToAssignCategory}
        onApplyTags={handleGlobalApplyTags}
        showApplyTags={Object.keys(rowSelection).length > 0}
        selectedEntities={selectedEntityCategories}
      />
      {loading ? (
        <ContentLoader />
      ) : namedBucketDefs.length === 0 ? (
        <DictionaryNoResults
          entityType={DictionaryTypeEnum.DictionaryBuckets}
        />
      ) : (
        <BinningsTable
          binnings={namedBucketDefs}
          schemaId={selectedSchemaId}
          onApplyTags={handleApplyTags}
          onSetFavorite={onSetFavorite}
          onNameChange={onNameChange}
          onDescriptionChange={onDescriptionChange}
          onDelete={handleDeleteNamedBucket}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          isPermittedToAssignCategory={isPermittedToAssignCategory}
          isPermittedToEditBinning={isPermittedToEditBinning}
          isPermittedToDeleteBinning={isPermittedToDeleteBinning}
        />
      )}
      {isDeleteModalOpen && <DeleteBinningModal />}
    </>
  );
};

export default BinningDictionary;
